import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import {
  Plus, Trash2, Edit, Eye, EyeOff, Upload, ChevronDown, ChevronUp,
  Video, Image, BookOpen, GripVertical, Check, X, Play, Users, FolderOpen, BarChart3, Globe, Link
} from 'lucide-react';
import CourseAnalytics from './CourseAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

const CourseManagement = ({ sessionToken }) => {
  const [view, setView] = useState('list'); // list, create, edit, manage, analytics
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '', description: '', about: '', category_id: '', price: '', original_price: '',
    validity_days: '365', language: 'Hindi', level: 'Beginner', what_you_learn: [''], is_published: false
  });
  const [saving, setSaving] = useState(false);
  const [sections, setSections] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonDuration, setNewLessonDuration] = useState('');
  const [addingLessonToSection, setAddingLessonToSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [externalLinks, setExternalLinks] = useState([]);
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/courses/admin/courses?session_token=${sessionToken}`),
        fetch(`${API_URL}/api/courses/categories?active_only=false`)
      ]);
      const coursesData = await coursesRes.json();
      const catsData = await catsRes.json();
      setCourses(coursesData.courses || []);
      setCategories(catsData.categories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetail = async (courseId) => {
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${courseId}?session_token=${sessionToken}`);
      const data = await res.json();
      setSelectedCourse(data.course);
      setSections(data.course.sections || []);
      const expanded = {};
      data.course.sections?.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
    } catch (err) {
      console.error(err);
    }
  };

  // ---- COURSE CRUD ----
  const saveCourse = async () => {
    setSaving(true);
    try {
      const body = {
        ...courseForm,
        price: parseFloat(courseForm.price) || 0,
        original_price: parseFloat(courseForm.original_price) || 0,
        validity_days: parseInt(courseForm.validity_days) || 365,
        what_you_learn: courseForm.what_you_learn.filter(w => w.trim()),
      };
      const isEdit = selectedCourse?.id;
      const url = isEdit
        ? `${API_URL}/api/courses/admin/courses/${selectedCourse.id}?session_token=${sessionToken}`
        : `${API_URL}/api/courses/admin/courses?session_token=${sessionToken}`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.status === 'success') {
        if (!isEdit && data.course) {
          setSelectedCourse(data.course);
          setView('manage');
        } else {
          setView('list');
        }
        fetchData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course and all its content?')) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/courses/${courseId}?session_token=${sessionToken}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const togglePublish = async (course) => {
    try {
      await fetch(`${API_URL}/api/courses/admin/courses/${course.id}?session_token=${sessionToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_published: !course.is_published })
      });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  // ---- CATEGORIES ----
  const addCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/categories?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      setNewCategoryName('');
      setShowCategoryForm(false);
      const res = await fetch(`${API_URL}/api/courses/categories?active_only=false`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleCategoryActive = async (cat) => {
    try {
      await fetch(`${API_URL}/api/courses/admin/categories/${cat.id}?session_token=${sessionToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !cat.is_active })
      });
      const res = await fetch(`${API_URL}/api/courses/categories?active_only=false`);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  // ---- SECTIONS ----
  const addSection = async () => {
    if (!newSectionTitle.trim() || !selectedCourse) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/courses/${selectedCourse.id}/sections?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newSectionTitle.trim() })
      });
      setNewSectionTitle('');
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error(err);
    }
  };

  const updateSection = async (sectionId) => {
    if (!editingSectionTitle.trim()) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/sections/${sectionId}?session_token=${sessionToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingSectionTitle.trim() })
      });
      setEditingSectionId(null);
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section and all its lessons?')) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/sections/${sectionId}?session_token=${sessionToken}`, { method: 'DELETE' });
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error(err);
    }
  };

  // ---- LESSONS ----
  const addLesson = async (sectionId) => {
    if (!newLessonTitle.trim()) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/sections/${sectionId}/lessons?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newLessonTitle.trim(),
          duration_seconds: parseInt(newLessonDuration) * 60 || 0
        })
      });
      setNewLessonTitle('');
      setNewLessonDuration('');
      setAddingLessonToSection(null);
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error(err);
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      await fetch(`${API_URL}/api/courses/admin/lessons/${lessonId}?session_token=${sessionToken}`, { method: 'DELETE' });
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error(err);
    }
  };

  // ---- CHUNKED VIDEO UPLOAD ----
  const uploadVideo = async (lessonId, file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    setUploadProgress(prev => ({ ...prev, [lessonId]: { progress: 0, status: 'Initializing...' } }));

    try {
      // 1. Init upload
      const initRes = await fetch(`${API_URL}/api/courses/admin/upload/init?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'video/mp4',
          total_chunks: totalChunks
        })
      });
      const { upload_id } = await initRes.json();

      // 2. Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append('upload_id', upload_id);
        formData.append('chunk_number', i);
        formData.append('file', chunk);

        await fetch(`${API_URL}/api/courses/admin/upload/chunk`, {
          method: 'POST',
          body: formData
        });

        const pct = Math.round(((i + 1) / totalChunks) * 90);
        setUploadProgress(prev => ({ ...prev, [lessonId]: { progress: pct, status: `Uploading... ${pct}%` } }));
      }

      // 3. Complete upload
      setUploadProgress(prev => ({ ...prev, [lessonId]: { progress: 95, status: 'Finalizing...' } }));
      const completeRes = await fetch(`${API_URL}/api/courses/admin/upload/complete?session_token=${sessionToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id,
          target_type: 'video',
          target_id: lessonId
        })
      });
      const completeData = await completeRes.json();
      if (completeData.status === 'success') {
        setUploadProgress(prev => ({ ...prev, [lessonId]: { progress: 100, status: 'Uploaded!' } }));
        fetchCourseDetail(selectedCourse.id);
        setTimeout(() => {
          setUploadProgress(prev => {
            const next = { ...prev };
            delete next[lessonId];
            return next;
          });
        }, 3000);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadProgress(prev => ({ ...prev, [lessonId]: { progress: 0, status: 'Upload failed!' } }));
    }
  };

  // ---- THUMBNAIL UPLOAD ----
  const uploadThumbnail = async (file) => {
    if (!selectedCourse) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('course_id', selectedCourse.id);
    formData.append('session_token', sessionToken);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/upload/thumbnail`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchCourseDetail(selectedCourse.id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const uploadTrailer = async (file) => {
    if (!selectedCourse) return;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    setUploadProgress(p => ({ ...p, trailer: { progress: 0, status: 'Uploading trailer...' } }));
    try {
      const initRes = await fetch(`${API_URL}/api/courses/admin/upload/init?session_token=${sessionToken}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'video/mp4', total_chunks: totalChunks })
      });
      const { upload_id } = await initRes.json();
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append('upload_id', upload_id);
        fd.append('chunk_number', String(i));
        fd.append('session_token', sessionToken);
        fd.append('file', chunk);
        await fetch(`${API_URL}/api/courses/admin/upload/chunk`, { method: 'POST', body: fd });
        setUploadProgress(p => ({ ...p, trailer: { progress: Math.round(((i + 1) / totalChunks) * 100), status: 'Uploading trailer...' } }));
      }
      await fetch(`${API_URL}/api/courses/admin/upload/complete?session_token=${sessionToken}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upload_id, target_type: 'trailer', target_id: selectedCourse.id })
      });
      setUploadProgress(p => ({ ...p, trailer: { progress: 100, status: 'Done!' } }));
      fetchCourseDetail(selectedCourse.id);
    } catch (err) {
      console.error('Trailer upload failed:', err);
      setUploadProgress(p => ({ ...p, trailer: { progress: 0, status: 'Failed' } }));
    }
  };

  const addExternalLink = async () => {
    if (!selectedCourse || !newLinkUrl.trim()) return;
    const links = [...(selectedCourse.external_links || []), { title: newLinkTitle.trim() || newLinkUrl.trim(), url: newLinkUrl.trim() }];
    await fetch(`${API_URL}/api/courses/admin/courses/${selectedCourse.id}?session_token=${sessionToken}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ external_links: links })
    });
    setNewLinkTitle('');
    setNewLinkUrl('');
    fetchCourseDetail(selectedCourse.id);
  };

  const removeExternalLink = async (idx) => {
    if (!selectedCourse) return;
    const links = [...(selectedCourse.external_links || [])];
    links.splice(idx, 1);
    await fetch(`${API_URL}/api/courses/admin/courses/${selectedCourse.id}?session_token=${sessionToken}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ external_links: links })
    });
    fetchCourseDetail(selectedCourse.id);
  };

  const startEdit = (course) => {
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      about: course.about || '',
      category_id: course.category_id || '',
      price: String(course.price || ''),
      original_price: String(course.original_price || ''),
      validity_days: String(course.validity_days || 365),
      language: course.language || 'Hindi',
      level: course.level || 'Beginner',
      what_you_learn: course.what_you_learn?.length > 0 ? course.what_you_learn : [''],
      is_published: course.is_published || false
    });
    setSelectedCourse(course);
    setView('edit');
  };

  const startManage = async (course) => {
    setSelectedCourse(course);
    await fetchCourseDetail(course.id);
    setView('manage');
  };

  const resetForm = () => {
    setCourseForm({
      title: '', description: '', about: '', category_id: '', price: '', original_price: '',
      validity_days: '365', language: 'Hindi', level: 'Beginner', what_you_learn: [''], is_published: false
    });
    setSelectedCourse(null);
  };

  // ==== RENDER FUNCTIONS ====

  const renderCourseList = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold" data-testid="courses-management-title">Courses ({courses.length})</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setView('analytics')} data-testid="course-analytics-btn">
            <BarChart3 className="w-4 h-4 mr-1" /> Analytics
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowCategoryForm(!showCategoryForm)} data-testid="manage-categories-btn">
            <FolderOpen className="w-4 h-4 mr-1" /> Categories
          </Button>
          <Button size="sm" onClick={() => { resetForm(); setView('create'); }} data-testid="create-course-btn">
            <Plus className="w-4 h-4 mr-1" /> New Course
          </Button>
        </div>
      </div>

      {/* Category Management */}
      {showCategoryForm && (
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <h3 className="font-medium mb-3">Categories</h3>
            <div className="space-y-2 mb-3">
              {categories.map(cat => (
                <div key={cat.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className={cat.is_active ? '' : 'text-gray-400 line-through'}>{cat.name}</span>
                  <div className="flex items-center gap-2">
                    <Switch checked={cat.is_active} onCheckedChange={() => toggleCategoryActive(cat)} data-testid={`toggle-cat-${cat.id}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="New category name"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                className="flex-1"
                data-testid="new-category-input"
              />
              <Button size="sm" onClick={addCategory} data-testid="add-category-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Course Cards */}
      {courses.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No courses yet. Create your first course!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map(course => (
            <Card key={course.id} className="hover:shadow-md transition-shadow" data-testid={`admin-course-${course.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{course.title}</h3>
                      <Badge variant={course.is_published ? "default" : "secondary"} className={course.is_published ? "bg-green-100 text-green-800" : ""}>
                        {course.is_published ? 'Published' : 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{course.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span>{course.section_count || 0} sections</span>
                      <span>{course.lesson_count || 0} lessons</span>
                      <span>{course.enrollment_count || 0} enrolled</span>
                      <span className="font-medium text-green-600">₹{course.price}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => togglePublish(course)} title={course.is_published ? 'Unpublish' : 'Publish'} data-testid={`toggle-publish-${course.id}`}>
                      {course.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startManage(course)} title="Manage Content" data-testid={`manage-course-${course.id}`}>
                      <Video className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(course)} title="Edit" data-testid={`edit-course-${course.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteCourse(course.id)} title="Delete" className="text-red-500 hover:text-red-700" data-testid={`delete-course-${course.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderCourseForm = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{view === 'create' ? 'Create New Course' : 'Edit Course'}</h2>
        <Button variant="ghost" size="sm" onClick={() => { setView('list'); resetForm(); }}>
          <X className="w-4 h-4 mr-1" /> Cancel
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Course Title *</label>
          <Input value={courseForm.title} onChange={e => setCourseForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. BTST Trading in Stock Options & Futures" data-testid="course-title-input" />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Short Description</label>
          <textarea
            value={courseForm.description}
            onChange={e => setCourseForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Brief description for course card"
            className="w-full px-3 py-2 border rounded-md text-sm resize-none h-20"
            data-testid="course-desc-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-sm font-medium">About (Detailed)</label>
          <textarea
            value={courseForm.about}
            onChange={e => setCourseForm(p => ({ ...p, about: e.target.value }))}
            placeholder="Detailed course description..."
            className="w-full px-3 py-2 border rounded-md text-sm resize-none h-32"
            data-testid="course-about-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium">Category</label>
          <select
            value={courseForm.category_id}
            onChange={e => setCourseForm(p => ({ ...p, category_id: e.target.value }))}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="course-category-select"
          >
            <option value="">Select Category</option>
            {categories.filter(c => c.is_active).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Level</label>
          <select
            value={courseForm.level}
            onChange={e => setCourseForm(p => ({ ...p, level: e.target.value }))}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="course-level-select"
          >
            <option value="Beginner">Beginner</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Advanced">Advanced</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Price (₹) *</label>
          <Input type="number" value={courseForm.price} onChange={e => setCourseForm(p => ({ ...p, price: e.target.value }))} placeholder="799" data-testid="course-price-input" />
        </div>
        <div>
          <label className="text-sm font-medium">Original Price (₹) for showing discount</label>
          <Input type="number" value={courseForm.original_price} onChange={e => setCourseForm(p => ({ ...p, original_price: e.target.value }))} placeholder="1999" data-testid="course-original-price-input" />
        </div>
        <div>
          <label className="text-sm font-medium">Validity (Days)</label>
          <Input type="number" value={courseForm.validity_days} onChange={e => setCourseForm(p => ({ ...p, validity_days: e.target.value }))} data-testid="course-validity-input" />
        </div>
        <div>
          <label className="text-sm font-medium">Language</label>
          <select
            value={courseForm.language}
            onChange={e => setCourseForm(p => ({ ...p, language: e.target.value }))}
            className="w-full px-3 py-2 border rounded-md text-sm"
            data-testid="course-language-select"
          >
            <option value="Hindi">Hindi</option>
            <option value="English">English</option>
            <option value="Tamil">Tamil</option>
            <option value="Telugu">Telugu</option>
            <option value="Marathi">Marathi</option>
          </select>
        </div>

        {/* What You'll Learn */}
        <div className="md:col-span-2">
          <label className="text-sm font-medium">What You Will Learn</label>
          <div className="space-y-2 mt-1">
            {courseForm.what_you_learn.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  value={item}
                  onChange={e => {
                    const arr = [...courseForm.what_you_learn];
                    arr[idx] = e.target.value;
                    setCourseForm(p => ({ ...p, what_you_learn: arr }));
                  }}
                  placeholder={`Learning point ${idx + 1}`}
                  data-testid={`learn-point-${idx}`}
                />
                {courseForm.what_you_learn.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => {
                    setCourseForm(p => ({ ...p, what_you_learn: p.what_you_learn.filter((_, i) => i !== idx) }));
                  }} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setCourseForm(p => ({ ...p, what_you_learn: [...p.what_you_learn, ''] }))} data-testid="add-learn-point-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Point
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button onClick={saveCourse} disabled={saving || !courseForm.title} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-course-btn">
          {saving ? 'Saving...' : view === 'create' ? 'Create Course' : 'Save Changes'}
        </Button>
        <Button variant="outline" onClick={() => { setView('list'); resetForm(); }}>Cancel</Button>
      </div>
    </div>
  );

  const renderManageContent = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" data-testid="manage-course-title">Manage: {selectedCourse?.title}</h2>
          <p className="text-sm text-gray-500">Manage modules, sub-modules, trailer and upload videos</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedCourse(null); }}>
          <X className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      {/* Thumbnail Upload */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Image className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-sm">Course Thumbnail</p>
                <p className="text-xs text-gray-400">{selectedCourse?.thumbnail_path ? 'Uploaded' : 'No thumbnail'}</p>
              </div>
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { if (e.target.files[0]) uploadThumbnail(e.target.files[0]); }}
                data-testid="thumbnail-upload-input"
              />
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors">
                <Upload className="w-4 h-4" /> {selectedCourse?.thumbnail_path ? 'Replace' : 'Upload'}
              </span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Trailer Video Upload */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Play className="w-5 h-5 text-purple-400" />
              <div>
                <p className="font-medium text-sm">Intro / Trailer Video</p>
                <p className="text-xs text-gray-400">
                  {selectedCourse?.trailer_video_path ? 'Trailer uploaded - plays on hover in course listing' : 'No trailer - upload a short intro video'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {uploadProgress.trailer && uploadProgress.trailer.progress < 100 && (
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${uploadProgress.trailer.progress}%` }} />
                  </div>
                  <span className="text-xs text-gray-500">{uploadProgress.trailer.progress}%</span>
                </div>
              )}
              {selectedCourse?.trailer_video_path && (
                <Badge className="bg-green-50 text-green-700 border-green-200 text-xs">Uploaded</Badge>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="video/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadTrailer(e.target.files[0]); }} data-testid="trailer-upload-input" />
                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 rounded-md text-sm text-purple-700 transition-colors">
                  <Upload className="w-4 h-4" /> {selectedCourse?.trailer_video_path ? 'Replace' : 'Upload Trailer'}
                </span>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* External Links */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-5 h-5 text-blue-400" />
            <p className="font-medium text-sm">External Links</p>
            <span className="text-xs text-gray-400">({selectedCourse?.external_links?.length || 0} links)</span>
          </div>
          {selectedCourse?.external_links?.length > 0 && (
            <div className="space-y-2 mb-3">
              {selectedCourse.external_links.map((link, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 p-2 rounded border">
                  <Link className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{link.title}</p>
                    <p className="text-xs text-gray-400 truncate">{link.url}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeExternalLink(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input value={newLinkTitle} onChange={e => setNewLinkTitle(e.target.value)} placeholder="Link title" className="flex-1" data-testid="link-title-input" />
            <Input value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)} placeholder="https://..." className="flex-1" data-testid="link-url-input" />
            <Button size="sm" onClick={addExternalLink} disabled={!newLinkUrl.trim()} data-testid="add-link-btn">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modules & Sub-Modules */}
      <div className="space-y-2">
        {sections.map((section, sIdx) => (
          <Card key={section.id} data-testid={`manage-section-${sIdx}`}>
            <CardContent className="p-0">
              {/* Section Header */}
              <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                {editingSectionId === section.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={editingSectionTitle}
                      onChange={e => setEditingSectionTitle(e.target.value)}
                      className="h-8 text-sm"
                      autoFocus
                      data-testid="edit-section-title-input"
                    />
                    <Button size="sm" variant="ghost" onClick={() => updateSection(section.id)} data-testid="save-section-title-btn">
                      <Check className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingSectionId(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))}
                      className="flex items-center gap-2 text-sm font-medium flex-1 text-left"
                    >
                      {expandedSections[section.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      Section {sIdx + 1}: {section.title}
                      <span className="text-xs text-gray-400 ml-2">({section.lessons?.length || 0} sub-modules)</span>
                    </button>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditingSectionId(section.id); setEditingSectionTitle(section.title); }}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteSection(section.id)} className="text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Lessons */}
              {expandedSections[section.id] && (
                <div className="divide-y">
                  {section.lessons?.map((lesson, lIdx) => (
                    <div key={lesson.id} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50" data-testid={`lesson-item-${lIdx}`}>
                      <Play className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm">{lesson.title}</span>
                        {lesson.duration_seconds > 0 && (
                          <span className="text-xs text-gray-400 ml-2">{Math.ceil(lesson.duration_seconds / 60)} min</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {lesson.video_storage_path ? (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Video</Badge>
                        ) : uploadProgress[lesson.id] ? (
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${uploadProgress[lesson.id].progress}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-500">{uploadProgress[lesson.id].status}</span>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={e => { if (e.target.files[0]) uploadVideo(lesson.id, e.target.files[0]); }}
                              data-testid={`upload-video-${lesson.id}`}
                            />
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-600 transition-colors">
                              <Upload className="w-3 h-3" /> Upload
                            </span>
                          </label>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteLesson(lesson.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Add Lesson */}
                  {addingLessonToSection === section.id ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50">
                      <Input
                        value={newLessonTitle}
                        onChange={e => setNewLessonTitle(e.target.value)}
                        placeholder="Lesson title"
                        className="h-8 text-sm flex-1"
                        autoFocus
                        data-testid="new-lesson-title-input"
                      />
                      <Input
                        value={newLessonDuration}
                        onChange={e => setNewLessonDuration(e.target.value)}
                        placeholder="Min"
                        type="number"
                        className="h-8 text-sm w-16"
                        data-testid="new-lesson-duration-input"
                      />
                      <Button size="sm" onClick={() => addLesson(section.id)} data-testid="save-lesson-btn">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAddingLessonToSection(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingLessonToSection(section.id); setNewLessonTitle(''); setNewLessonDuration(''); }}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 w-full text-left"
                      data-testid={`add-lesson-to-${section.id}`}
                    >
                      <Plus className="w-4 h-4" /> Add Sub-Module
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Section */}
      <div className="flex gap-2">
        <Input
          value={newSectionTitle}
          onChange={e => setNewSectionTitle(e.target.value)}
          placeholder="New module title (e.g. Introduction)"
          onKeyDown={e => e.key === 'Enter' && addSection()}
          data-testid="new-section-input"
        />
        <Button onClick={addSection} disabled={!newSectionTitle.trim()} data-testid="add-section-btn">
          <Plus className="w-4 h-4 mr-1" /> Module
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  if (view === 'create' || view === 'edit') return renderCourseForm();
  if (view === 'manage') return renderManageContent();
  if (view === 'analytics') return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Course Analytics</h2>
        <Button size="sm" variant="outline" onClick={() => setView('list')} data-testid="back-to-courses-btn">
          Back to Courses
        </Button>
      </div>
      <CourseAnalytics sessionToken={sessionToken} />
    </div>
  );
  return renderCourseList();
};

export default CourseManagement;
